import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Card, Collapse, Button, Badge, Tag, Descriptions, Divider, Tabs } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { injectIntl, useIntl } from 'react-intl';
import { AnyAction, compose } from '@reduxjs/toolkit';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import { requestGetWithdrawals, resetState, requestPutWithdrawals, requestGetRecentTransactions } from './reducer';
import {
  selectWithdrawals,
  selectWithdrawalsLoading,
  selectGetWithdrawalsError,
  selectRecentTransactions,
  selectRecentTransactionsLoading
} from './selectors';
import { T } from '@app/components/T';
import { translationMessages } from '@app/i18n';
import { If } from '@app/components';

const PageContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 24px;
`;

const WithdrawalsTitle = styled.h1``;

const CardContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 15px;
`;

const StatisticsCard = styled(Card)`
  width: 23%;
`;

const { Panel } = Collapse;
const { TabPane } = Tabs;

const WithdrawalAccordion = styled(Collapse)`
  margin-top: 2rem;

  .ant-collapse-item {
    margin-bottom: 16px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;

    &:last-child {
      border-radius: 8px;
    }
  }

  .ant-collapse-header {
    background: #fafafa;
    border-radius: 8px 8px 0 0 !important;

    &:hover {
      background: #f0f0f0;
    }
  }

  .ant-collapse-content {
    border-top: 1px solid #e8e8e8;
  }
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: 24px;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AmountInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const DetailContainer = styled.div`
  padding: 16px;
`;

const TabContainer = styled.div`
  margin-top: 16px;
`;

type WithdrawalContainerProps = {
  dispatchPutWithdrawals(user: any): AnyAction;
  withdrawals: any;
  withdrawalsLoading: boolean;
  dispatchGetWithdrawals: (date: string) => AnyAction;
  dispatchResetState: () => AnyAction;
  getWithdrawalsError: string;
  recentTransactions: any;
  recentTransactionsLoading: boolean;
  dispatchGetRecentTransactions: (userId: string) => AnyAction;
};

const WithdrawalContainer = (props: WithdrawalContainerProps) => {
  const intl = useIntl();

  useEffect(() => {
    props.dispatchGetWithdrawals('');
  }, []);

  const fetchWithdrawals = (date: any) => {
    props.dispatchGetWithdrawals(date.valueOf());
  };

  const processPayment = (details: any) => {
    confirm(`Are you sure you want to process this payment?`) &&
      props.dispatchPutWithdrawals({ details, withdrawals: props.withdrawals });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'green';
      case 'pending':
      case 'initiated':
        return 'orange';
      case 'failed':
        return 'red';
      default:
        return 'default';
    }
  };

  const renderAccordionHeader = (withdrawal: any) => {
    const { userDetails, transactionDetails } = withdrawal;
    const fullName = `${userDetails.firstName} ${userDetails.lastName}`;

    return (
      <AccordionHeader>
        <UserInfo>
          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{fullName}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {moment.utc(transactionDetails.createdAt).tz('America/Chicago').format('DD/MM/YYYY HH:mm')}
          </div>
        </UserInfo>

        <AmountInfo>
          <div style={{ fontWeight: 'bold', fontSize: '18px', color: '#1890ff' }}>
            ${Number(transactionDetails.amount.$numberDecimal).toFixed(2)}
          </div>
          <Badge status={getStatusColor(transactionDetails.status)} text={transactionDetails.status} />
        </AmountInfo>
      </AccordionHeader>
    );
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'deposit':
        return 'green';
      case 'withdrawal':
        return 'orange';
      case 'wagerwon':
        return 'blue';
      case 'wagerlost':
        return 'red';
      default:
        return 'default';
    }
  };

  const formatTransactionType = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'wagerwon':
        return 'Wager Won';
      case 'wagerlost':
        return 'Wager Lost';
      case 'deposit':
        return 'Deposit';
      case 'withdrawal':
        return 'Withdrawal';
      default:
        return type;
    }
  };

  const handleTabChange = (key: string, userDetails: any) => {
    if (key === 'recent' && userDetails.userId) {
      const userId = userDetails.userId;
      // Check if we already have recent transactions for this user
      if (!props.recentTransactions[userId]) {
        props.dispatchGetRecentTransactions(userId);
      }
    }
  };

  const renderRecentTransactions = (userDetails: any) => {
    const userId = userDetails.userId;
    const userRecentTransactions = props.recentTransactions[userId] || [];

    if (props.recentTransactionsLoading) {
      return (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <div>Loading recent transactions...</div>
        </div>
      );
    }

    if (!userRecentTransactions || userRecentTransactions.length === 0) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
          <div>No recent transactions found for this user</div>
        </div>
      );
    }

    return (
      <div style={{ padding: '16px' }}>
        {userRecentTransactions.map((transaction: any, index: number) => (
          <div
            key={transaction._id || index}
            style={{ marginBottom: '16px', padding: '12px', border: '1px solid #f0f0f0', borderRadius: '6px' }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}
            >
              <Tag color={getTransactionTypeColor(transaction.type)}>{formatTransactionType(transaction.type)}</Tag>
              <div
                style={{
                  fontWeight: 'bold',
                  color:
                    transaction.type?.toLowerCase() === 'deposit' || transaction.type?.toLowerCase() === 'wagerwon'
                      ? '#52c41a'
                      : '#ff4d4f'
                }}
              >
                {transaction.type?.toLowerCase() === 'deposit' || transaction.type?.toLowerCase() === 'wagerwon'
                  ? '+'
                  : '-'}
                ${Number(transaction.amount?.$numberDecimal || transaction.amount || 0).toFixed(2)}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '12px',
                color: '#666'
              }}
            >
              <Badge status={getStatusColor(transaction.status)} text={transaction.status} />
              <div>{moment.utc(transaction.createdAt).tz('America/Chicago').format('MMM DD, YYYY HH:mm')}</div>
            </div>

            {transaction.fundingSource && (transaction.fundingSource.scheme || transaction.fundingSource.last4) && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                {transaction.fundingSource.scheme && (
                  <Tag size="small" color="blue" style={{ textTransform: 'capitalize' }}>
                    {transaction.fundingSource.scheme}
                  </Tag>
                )}
                {transaction.fundingSource.last4 && <span>•••• {transaction.fundingSource.last4}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const handleOnfidoDashboardClick = (onfidoApplicantId: string) => {
    if (onfidoApplicantId) {
      const onfidoUrl = `https://dashboard.us.onfido.com/results/?page=1&search=${onfidoApplicantId}&sort=desc`;
      window.open(onfidoUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const formatPhoneNumber = (phone: string | number) => {
    if (!phone) return '';
    const cleaned = String(phone).replace(/\D/g, '');
    if (cleaned.length !== 10) return String(phone);
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : String(phone);
  };

  const renderAccordionContent = (withdrawal: any) => {
    const { userDetails, transactionDetails } = withdrawal;

    return (
      <DetailContainer>
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="User ID">{userDetails.userId}</Descriptions.Item>
          <Descriptions.Item label="Email">{userDetails.userEmail}</Descriptions.Item>
          <Descriptions.Item label="Mobile">{formatPhoneNumber(userDetails.mobileNumber)}</Descriptions.Item>
          <Descriptions.Item label="Available Balance">
            ${Number(userDetails.balance.$numberDecimal).toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Withdrawal App">
            <Tag color="blue" style={{ textTransform: 'capitalize' }}>
              {transactionDetails.withdrawalApp || 'N/A'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Contact Method">
            <Tag color="green">{transactionDetails.contactMethod || 'N/A'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Onfido Dashboard">
            {userDetails.onfidoApplicantId ? (
              <Button
                type="link"
                icon={<LinkOutlined />}
                onClick={() => handleOnfidoDashboardClick(userDetails.onfidoApplicantId)}
                style={{ padding: 0 }}
              >
                View on Onfido
              </Button>
            ) : (
              <span style={{ color: '#999' }}>Not Available</span>
            )}
          </Descriptions.Item>
        </Descriptions>

        <TabContainer>
          <Tabs defaultActiveKey="details" onChange={(key) => handleTabChange(key, userDetails)}>
            <TabPane tab="Transaction Details" key="details">
              <div style={{ padding: '16px 0' }}>
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="Transaction ID">{transactionDetails.transactionId}</Descriptions.Item>
                  <Descriptions.Item label="Created">
                    {moment.utc(transactionDetails.createdAt).tz('America/Chicago').format('DD/MM/YYYY HH:mm:ss z')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Updated">
                    {moment.utc(transactionDetails.updatedAt).tz('America/Chicago').format('DD/MM/YYYY HH:mm:ss z')}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </TabPane>
            <TabPane tab="Recent Transactions" key="recent">
              {renderRecentTransactions(userDetails)}
            </TabPane>
          </Tabs>
        </TabContainer>

        <Divider />

        <div style={{ textAlign: 'center' }}>
          <If condition={transactionDetails.status !== 'completed'} otherwise={<Tag color="green">Processed</Tag>}>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                processPayment(withdrawal);
              }}
            >
              <T id="process_payment" />
            </Button>
          </If>
        </div>
      </DetailContainer>
    );
  };

  return (
    <PageContainer>
      <WithdrawalsTitle>
        <T id="withdrawals" />
      </WithdrawalsTitle>

      <WithdrawalAccordion ghost={false} size="large">
        {props.withdrawals?.map((withdrawal: any, index: number) => (
          <Panel
            key={withdrawal.transactionDetails?.transactionId || index}
            header={renderAccordionHeader(withdrawal)}
            showArrow={true}
          >
            {renderAccordionContent(withdrawal)}
          </Panel>
        ))}
      </WithdrawalAccordion>

      {props.withdrawalsLoading && <div style={{ textAlign: 'center', padding: '50px' }}>Loading withdrawals...</div>}

      {!props.withdrawalsLoading && (!props.withdrawals || props.withdrawals.length === 0) && (
        <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>No withdrawal requests found</div>
      )}
    </PageContainer>
  );
};

WithdrawalContainer.propTypes = {
  withdrawals: PropTypes.array,
  withdrawalsLoading: PropTypes.bool,
  getWithdrawalsError: PropTypes.string,
  dispatchGetWithdrawals: PropTypes.func,
  dispatchResetState: PropTypes.func,
  dispatchPutWithdrawals: PropTypes.func,
  recentTransactions: PropTypes.object,
  recentTransactionsLoading: PropTypes.bool,
  dispatchGetRecentTransactions: PropTypes.func
};

const mapStateToProps = createStructuredSelector({
  withdrawals: selectWithdrawals(),
  withdrawalsLoading: selectWithdrawalsLoading(),
  getWithdrawalsError: selectGetWithdrawalsError(),
  recentTransactions: selectRecentTransactions(),
  recentTransactionsLoading: selectRecentTransactionsLoading()
});

export function mapDispatchToProps(dispatch: (arg0: AnyAction) => any) {
  return {
    dispatchGetWithdrawals: (date: string) => dispatch(requestGetWithdrawals({ date })),
    dispatchPutWithdrawals: (details: any) => dispatch(requestPutWithdrawals({ details })),
    dispatchResetState: () => dispatch(resetState()),
    dispatchGetRecentTransactions: (userId: string) => dispatch(requestGetRecentTransactions({ userId }))
  };
}
const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect, injectIntl)(WithdrawalContainer);
